import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, Fingerprint, Lock, AlertTriangle, RefreshCw, Crown } from "lucide-react";
import api from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const DOT = { green: "bg-emerald-500", yellow: "bg-yellow-500", red: "bg-destructive" };
const LABEL = { green: "Live", yellow: "Partial", red: "Not live" };
const E2EE_LABEL = { green: "Enabled", yellow: "Partial", red: "Off" };

function Stoplight({ status, labels, stale }) {
  const s = status || "red";
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`w-2.5 h-2.5 rounded-full ${DOT[s]} ${s === "green" ? "pulse-dot" : ""}`} />
      <span className="text-sm text-white">{labels[s] || s}</span>
      {stale && (
        <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border border-yellow-500/30 bg-yellow-500/5 text-yellow-500">
          <AlertTriangle className="w-2.5 h-2.5" /> stale
        </span>
      )}
    </span>
  );
}

export default function SsoDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["sso-dashboard"],
    queryFn: async () => (await api.get("/sso-dashboard")).data,
    refetchInterval: 60000,
  });

  const rows = data?.rows || [];
  const live = data?.summary?.live ?? 0;
  const total = data?.summary?.total ?? 0;
  const reg = data?.registry || {};
  const lastChecked = reg.last_checked ? new Date(reg.last_checked).toLocaleString() : "—";

  return (
    <div className="space-y-6 max-w-5xl" data-testid="sso-dashboard">
      {/* Summary */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-sm bg-primary/10 border border-primary/25 flex items-center justify-center">
            <Fingerprint className="w-6 h-6 text-primary" />
          </div>
          <div>
            <div className="font-mono text-3xl font-light text-white" data-testid="sso-summary">
              {live} <span className="text-muted-foreground text-xl">of {total}</span>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              Apps on Precipice ID
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-2 justify-end font-mono text-[11px] text-muted-foreground">
            <RefreshCw className="w-3 h-3" /> registry checked {lastChecked}
          </div>
          {reg.stale && (
            <div className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.08em] px-2 py-1 rounded-sm border border-yellow-500/30 bg-yellow-500/5 text-yellow-500" data-testid="registry-stale">
              <AlertTriangle className="w-3 h-3" /> registry unreachable · showing last known
            </div>
          )}
          <div className="font-mono text-[10px] text-muted-foreground mt-1">read-only · source of truth: Precipice ID</div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-border rounded-sm bg-card" data-testid="sso-table">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              {["App", "SSO (Precipice ID)", "E2EE", "Last Updated"].map((h, i) => (
                <TableHead key={i} className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-10">Loading…</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.slug} className="border-border hover:bg-white/[0.02]" data-testid={`sso-row-${r.slug}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white">{r.name}</span>
                    {r.is_host && (
                      <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border border-primary/30 bg-primary/5 text-primary">
                        <Crown className="w-2.5 h-2.5" /> host
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground">/{r.slug}</div>
                </TableCell>
                <TableCell><Stoplight status={r.sso_status} labels={LABEL} stale={r.sso_source === "stale"} /></TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${DOT[r.e2ee_status || "red"]}`} />
                    <span className="text-sm text-white">{E2EE_LABEL[r.e2ee_status || "red"]}</span>
                  </span>
                </TableCell>
                <TableCell className="font-mono text-[11px] text-muted-foreground">
                  {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground flex items-center gap-2">
        <Lock className="w-3.5 h-3.5" /> Client registrations are managed in the Precipice ID founder console. This view is read-only.
      </p>
    </div>
  );
}
